import type {
  DirectoryResponse,
  PlayerProfileResponse,
  TeamProfileResponse,
} from '@ballpeek/api/client'
import type { ZodType } from 'zod'
import type { EntityKind } from './types'

import {
  directoryResponseSchema,
  playerProfileResponseSchema,
  teamProfileResponseSchema,
} from '@ballpeek/api/client'
import * as Sentry from '@sentry/react'

import { getApiBaseUrl } from '~/lib/api-client'
import { readPublicFootballResponse } from '~/lib/public-football-read'

async function readPublicEntity<T>(
  path: string,
  schema: ZodType<T>,
  signal?: AbortSignal,
): Promise<T> {
  const response = await fetch(`${getApiBaseUrl()}${path}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  })
  if (!response.ok) {
    Sentry.addBreadcrumb({
      category: 'api_call',
      message: `Public football entity request failed: ${response.status}`,
      level: 'error',
      data: {
        api_request_id: response.headers.get('X-Request-Id'),
        path,
        status: response.status,
      },
    })
  }
  return readPublicFootballResponse(response, schema, 'Public football entity request failed')
}

export async function getDirectory(input: {
  q: string
  type: EntityKind
  page: number
  signal?: AbortSignal
}): Promise<DirectoryResponse['data']> {
  const params = new URLSearchParams({
    q: input.q.trim(),
    type: input.type,
    page: String(input.page),
  })
  return (await readPublicEntity(
    `/api/football/directory?${params}`,
    directoryResponseSchema,
    input.signal,
  )).data
}

export async function getTeamProfile(input: {
  teamId: string
  playerPage?: number
  signal?: AbortSignal
}): Promise<TeamProfileResponse['data']> {
  const params = new URLSearchParams({ playerPage: String(input.playerPage ?? 1) })
  return (await readPublicEntity(
    `/api/football/teams/${encodeURIComponent(input.teamId)}?${params}`,
    teamProfileResponseSchema,
    input.signal,
  )).data
}

export async function getPlayerProfile(input: {
  playerId: string
  appearancePage?: number
  at?: string
  signal?: AbortSignal
}): Promise<PlayerProfileResponse['data']> {
  const params = new URLSearchParams({ appearancePage: String(input.appearancePage ?? 1) })
  if (input.at)
    params.set('at', input.at)
  return (await readPublicEntity(
    `/api/football/players/${encodeURIComponent(input.playerId)}?${params}`,
    playerProfileResponseSchema,
    input.signal,
  )).data
}
